/**
 * The blocking yes/no, now rendered by Ant Design.
 *
 * The signature is the old ui/Modal.ts one, unchanged, so callers (the guest
 * and sign-out warnings) never knew the implementation moved. The two details
 * that were the whole point survive the move: the SAFE answer takes focus
 * (autoFocusButton: 'cancel'), and the walk is locked while the question is
 * open, so WASD cannot stroll the student away from a decision that is still
 * pending - now via the walk-lock rather than a hand-rolled key swallower.
 */

import { App as AntApp } from 'antd'
import { useEffect } from 'react'
import { acquireWalkLock } from '../input/walkLock'

export interface ConfirmOptions {
  title: string
  body: string
  /** The answer that does the thing. Not focused, on purpose. */
  okText: string
  cancelText: string
}

type ModalApi = ReturnType<typeof AntApp.useApp>['modal']

let modalApi: ModalApi | null = null

/**
 * Inside <AntApp/>, exposing its themed modal instance to plain-TS callers.
 * Renders nothing.
 */
export function ConfirmBridge() {
  const { modal } = AntApp.useApp()
  useEffect(() => {
    modalApi = modal
    return () => {
      if (modalApi === modal) modalApi = null
    }
  }, [modal])
  return null
}

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  const api = modalApi
  if (!api) {
    // The shell is not mounted (should never happen after boot); the safe
    // answer is the one that does nothing.
    return Promise.resolve(false)
  }
  return new Promise((resolve) => {
    const release = acquireWalkLock()
    let settled = false
    const finish = (answer: boolean): void => {
      if (settled) return
      settled = true
      release()
      resolve(answer)
    }
    api.confirm({
      title: opts.title,
      content: opts.body,
      okText: opts.okText,
      cancelText: opts.cancelText,
      autoFocusButton: 'cancel',
      maskClosable: true,
      icon: null,
      onOk: () => finish(true),
      onCancel: () => finish(false),
    })
  })
}
